import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Form, Input, Space, Table, Typography, notification } from 'antd';
import { SearchOutlined, PlusOutlined, SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import md5 from 'md5';
import { useSelector } from 'react-redux';
import { default as createApiGateway } from '../../../apiGateway';
import { buildCompanyLogin } from '../../../helpers/exchanges';

const apiGateway = createApiGateway();

const defaultCompany = {
  id: null,
  firstName: '',
  emailAddress: '',
  role: 'user',
};

function CompaniesPage() {
  const userInfo = useSelector((state) => state.login);

  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [form] = Form.useForm();

  const loadCompanies = async (name) => {
    try {
      setLoading(true);
      const all = await apiGateway.users.getAll({ allowedRoles: ['user'] });
      const filtered = Array.isArray(all)
        ? all.filter((u) =>
            name
              ? String(u.firstName || '')
                  .toLowerCase()
                  .includes(String(name).toLowerCase())
              : true
          )
        : [];
      setCompanies(filtered);
    } catch (e) {
      notification.error({ title: 'Failed to load companies', description: e?.message || String(e) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCompanies('');
  }, []);

  const onSearch = async () => {
    await loadCompanies(searchValue);
  };

  const onResetForm = () => {
    form.setFieldsValue({ ...defaultCompany });
  };

  const onCreate = async () => {
    try {
      const values = await form.validateFields();
      // Companies are always created in the user's own exchange
      const slug = userInfo.exchangeSlug;
      if (!slug) {
        notification.error({ title: 'Your exchange is not loaded yet, try again' });
        return;
      }
      const payload = {
        ...values,
        lastName: values.firstName,
        exchangeId: userInfo.exchangeId,
        login: buildCompanyLogin(slug, values.firstName),
        password: md5(Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)),
        role: 'user',
      };

      await apiGateway.users.createUser(payload);
      notification.success({ title: 'Company created' });
      onResetForm();
      await onSearch();
    } catch (e) {
      if (e?.errorFields) return; // form validation error
      notification.error({ title: 'Error creating company', description: e?.message || String(e) });
    }
  };

  const onUpdate = async () => {
    try {
      const values = await form.validateFields();
      if (!values.id) {
        notification.error({ title: 'Select a company to update' });
        return;
      }
      const payload = {
        ...values,
        lastName: values.firstName,
        role: 'user',
      };
      await apiGateway.users.updateUser(payload);
      notification.success({ title: 'Company updated' });
      onResetForm();
      await onSearch();
    } catch (e) {
      if (e?.errorFields) return; // form validation error
      notification.error({ title: 'Error updating company', description: e?.message || String(e) });
    }
  };

  const columns = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 90,
        sorter: (a, b) => (a.id || 0) - (b.id || 0),
      },
      {
        title: 'Company Name',
        dataIndex: 'firstName',
        key: 'firstName',
        ellipsis: true,
        sorter: (a, b) => (a.firstName || '').localeCompare(b.firstName || ''),
      },
      {
        title: 'Email',
        dataIndex: 'emailAddress',
        key: 'emailAddress',
        ellipsis: true,
        sorter: (a, b) => (a.emailAddress || '').localeCompare(b.emailAddress || ''),
      },
      {
        title: 'Updated At',
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        render: (val) => (val ? moment.utc(val).format('YYYY-MM-DD HH:mm:ss') : 'n/a'),
        sorter: (a, b) => (a.updatedAt || '').localeCompare(b.updatedAt || ''),
        defaultSortOrder: 'descend',
        width: 180,
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 120,
        render: (_, record) => (
          <Space size={8}>
            <Button
              size="small"
              type="link"
              onClick={() =>
                form.setFieldsValue({
                  id: record.id,
                  firstName: record.firstName || '',
                  emailAddress: record.emailAddress || '',
                })
              }
            >
              Edit
            </Button>
          </Space>
        ),
      },
    ],
    [form]
  );

  return (
    <div className="xchange-page">
      <Space orientation="vertical" size={12} style={{ width: '100%' }}>
        <Card size="small" styles={{ body: { padding: 12 } }} style={{ borderRadius: 6 }}>
          <Space
            wrap
            align="center"
            className="xchange-filters"
            style={{ width: '100%', justifyContent: 'space-between' }}
          >
            <div style={{ minWidth: 260, flex: 1, marginRight: 12 }}>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Search by company name</div>
              <Input
                placeholder="Enter company name"
                allowClear
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onPressEnter={onSearch}
              />
            </div>
            <Button type="primary" icon={<SearchOutlined />} onClick={onSearch} loading={loading}>
              Search
            </Button>
          </Space>
        </Card>

        <div className="xchange-split">
          <Card size="small" style={{ borderRadius: 6 }} styles={{ body: { padding: 12 } }}>
            <Table
              size="small"
              rowKey={(row) => `${row.id}`}
              dataSource={companies}
              columns={columns}
              loading={loading}
              pagination={false}
              scroll={{ x: 'max-content' }}
            />
          </Card>

          <Card size="small" style={{ borderRadius: 6 }} styles={{ body: { padding: 12 } }}>
            <Typography.Text strong>Create / Update Company</Typography.Text>
            <Form form={form} layout="vertical" initialValues={defaultCompany} style={{ marginTop: 12 }}>
              <Form.Item label="ID" name="id">
                <Input placeholder="Auto-filled when editing" disabled />
              </Form.Item>
              <Form.Item
                label="Company Name"
                name="firstName"
                rules={[{ required: true, message: 'Please enter company name' }]}
              >
                <Input placeholder="Enter company name" allowClear />
              </Form.Item>
              <Form.Item
                label="Email"
                name="emailAddress"
                rules={[{ required: true, type: 'email', message: 'Please enter valid email' }]}
              >
                <Input placeholder="Enter email" allowClear />
              </Form.Item>

              <Space>
                <Button type="primary" icon={<PlusOutlined />} onClick={onCreate}>
                  Create
                </Button>
                <Button icon={<SaveOutlined />} onClick={onUpdate}>
                  Update
                </Button>
                <Button icon={<ReloadOutlined />} onClick={onResetForm}>
                  Reset
                </Button>
              </Space>
            </Form>
          </Card>
        </div>
      </Space>
    </div>
  );
}

export default CompaniesPage;
